import { Badge, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@nexus/ui";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import {
  formatCompetitorsOfTotal,
  formatInvestmentMultiple,
  formatPercent,
} from "../../../lib/persona-opportunity-row-model.js";

const TOOLTIP_DELAY_MS = 300;
const MAX_LISTED_ANGLES = 8;

/** Group-level KPIs, known from the server rollup before the group's rows load. */
export interface PersonaOpportunityAngleStats {
  creativesCount: number;
  competitorsRunningCount: number;
  competitorsTotal: number;
  stickRatePct: number | null;
  investmentIndex: number | null;
}

/** Counts a persona group's angles. Hovering names them and lists the group's KPIs when known. */
export function PersonaOpportunityAngleCountBadge({
  title,
  angleCount,
  angles = [],
  stats = null,
  selectedCount = 0,
}: {
  title: string;
  angleCount: number;
  angles?: readonly string[];
  stats?: PersonaOpportunityAngleStats | null;
  /** Selected angles in the group, appended to the count when any are. */
  selectedCount?: number;
}) {
  const { t } = useTranslation();
  const listed = angles.slice(0, MAX_LISTED_ANGLES);
  const hiddenCount = angles.length - listed.length;
  const investment = stats ? formatInvestmentMultiple(stats.investmentIndex) : null;

  return (
    <TooltipProvider delayDuration={TOOLTIP_DELAY_MS}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            variant="slate"
            iconStart="target"
            className="text-[11px] tabular-nums"
            data-testid="persona-opportunity-angle-count-badge"
          >
            {t("personaOpportunities.active.angleCount", { count: angleCount })}
            {selectedCount > 0 ? (
              <>
                {" · "}
                {t("personaOpportunities.active.selectedCount", {
                  count: selectedCount,
                  defaultValue_one: "{{count}} selected",
                  defaultValue_other: "{{count}} selected",
                })}
              </>
            ) : null}
          </Badge>
        </TooltipTrigger>
        <TooltipContent align="start" className="max-w-80 px-3 py-2">
          <div className="flex flex-col gap-1.5" data-testid="persona-opportunity-angle-count-params">
            <p className="truncate font-medium">{title}</p>
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
              <AngleParam label={t("personaOpportunities.active.angles", "Angles")}>
                {angleCount}
              </AngleParam>
              {stats ? (
                <>
                  <AngleParam label={t("personaOpportunities.active.columns.creatives", "Creatives")}>
                    {stats.creativesCount}
                  </AngleParam>
                  <AngleParam
                    label={t("personaOpportunities.active.columns.competitors", "Competitors")}
                  >
                    {formatCompetitorsOfTotal(stats.competitorsRunningCount, stats.competitorsTotal)}
                  </AngleParam>
                  {stats.stickRatePct != null ? (
                    <AngleParam
                      label={t("personaOpportunities.active.columns.stickRate", "Stick rate")}
                    >
                      {formatPercent(stats.stickRatePct)}
                    </AngleParam>
                  ) : null}
                  {investment ? (
                    <AngleParam
                      label={t(
                        "personaOpportunities.active.columns.investmentIndex",
                        "Investment index",
                      )}
                    >
                      {investment}
                    </AngleParam>
                  ) : null}
                </>
              ) : null}
            </dl>
            {listed.length > 0 ? (
              <ul className="flex flex-col gap-0.5 pt-0.5">
                {listed.map((angle, index) => (
                  <li key={`${angle}-${index}`} className="truncate">
                    {angle}
                  </li>
                ))}
              </ul>
            ) : null}
            {hiddenCount > 0 ? (
              <p className="opacity-70">
                {t("personaOpportunities.active.anglesMore", {
                  count: hiddenCount,
                  defaultValue: "+{{count}} more",
                })}
              </p>
            ) : null}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function AngleParam({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="opacity-70">{label}</dt>
      <dd className="tabular-nums">{children}</dd>
    </>
  );
}
